import type {
  FlightFareBaggage,
  FlightFareChangeRefund,
  FlightFareLeg,
} from "../types";

export type ExtractedFlightFareDetailsBaggageItem = {
  count?: number;
  weight?: number;
  dimensions?: string;
};

export type ExtractedFlightFareDetails = {
  name: string;
  baggage: {
    carryOn: ExtractedFlightFareDetailsBaggageItem | null;
    checked: ExtractedFlightFareDetailsBaggageItem | null;
    smallBag: ExtractedFlightFareDetailsBaggageItem | null;
  } | null;
  flightChange: {
    variant: StrictExtract<
      FlightFareChangeRefund["variant"],
      "changeable" | "change_not_possible" | "changeable_with_fees"
    >;
    timing: FlightFareChangeRefund["timing"];
  } | null;
  flightRefund: {
    variant: StrictExtract<
      FlightFareChangeRefund["variant"],
      | "refundable"
      | "non_refundable"
      | "refundable_with_fees"
      | "refundable_as_credit"
      | "refundable_as_credit_with_fees"
    >;
    timing: FlightFareChangeRefund["timing"];
  } | null;
  loyaltyProgram: {
    name: string;
    percentage?: number;
  } | null;
  priority: {
    type: FlightFareLeg["priority"][number]["type"] | null;
  } | null;
  loungeAccess: {
    displayName: string;
  } | null;
};

export function extractDataFromFlightFare(
  source: FlightFareLeg,
): ExtractedFlightFareDetails | null {
  const name = source.brandName;
  if (!name) return null;

  let baggage: ExtractedFlightFareDetails["baggage"] = null;
  if (source.baggage.length) {
    baggage = {
      carryOn: extractBaggageItem(source.baggage, "carry_on"),
      checked: extractBaggageItem(source.baggage, "checked"),
      smallBag: extractBaggageItem(source.baggage, "small_bag"),
    };
  }

  let flightChange: ExtractedFlightFareDetails["flightChange"] = null;
  let flightRefund: ExtractedFlightFareDetails["flightRefund"] = null;
  if (source.changeRefund.length) {
    const change = source.changeRefund.find((c) => c.category === "change");

    // Type assertions below needed to narrow down the variant types from the more general FlightFareChangeRefund["variant"]
    // to the separated change and refund variant types defined in ExtractedFlightFareDetails
    if (change) {
      flightChange = {
        variant: change.variant as NonNullable<
          ExtractedFlightFareDetails["flightChange"]
        >["variant"],
        timing: change.timing,
      };
    }

    const refund = source.changeRefund.find((c) => c.category === "cancel");

    if (refund) {
      flightRefund = {
        variant: refund.variant as NonNullable<
          ExtractedFlightFareDetails["flightRefund"]
        >["variant"],
        timing: refund.timing,
      };
    }
  }

  let loyaltyProgram: ExtractedFlightFareDetails["loyaltyProgram"] = null;
  if (source.loyalty.length) {
    const name = source.loyalty[0].programName;

    if (name) {
      loyaltyProgram = {
        name,
        percentage: source.loyalty[0].percentage || undefined,
      };
    }
  }

  let priority: ExtractedFlightFareDetails["priority"] = null;
  if (source.priority.length && source.priority[0].type) {
    priority = {
      type: source.priority[0].type,
    };
  }

  let loungeAccess: ExtractedFlightFareDetails["loungeAccess"] = null;
  if (source.lounge.length && source.lounge[0].displayName) {
    loungeAccess = {
      displayName: source.lounge[0].displayName,
    };
  }

  return {
    name,
    baggage,
    flightChange,
    flightRefund,
    loyaltyProgram,
    priority,
    loungeAccess,
  };
}

function extractBaggageItem(
  baggages: FlightFareLeg["baggage"],
  type: FlightFareBaggage["baggageType"],
): ExtractedFlightFareDetailsBaggageItem | null {
  const maxWeightBaggage = baggages.reduce<FlightFareBaggage | null>(
    (max, baggage) => {
      if (baggage.baggageType !== type) return max;

      if (!max) return baggage;

      if (baggage.weightKg > (max?.weightKg ?? 0)) return baggage;

      return max;
    },
    null,
  );

  if (!maxWeightBaggage) return null;

  return {
    count: maxWeightBaggage.pieces || undefined,
    weight: maxWeightBaggage.weightKg || undefined,
    dimensions: maxWeightBaggage.dimensionsCm || undefined,
  };
}
